/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable column-width state.
 *
 * Holds only the widths the user has actually changed; anything untouched falls
 * back to the column's own `width`. When `storageKey` is set the overrides are
 * mirrored to localStorage so a table reopens the way the user left it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Smallest width a column can be dragged to, so a column can never vanish. */
export const MIN_COLUMN_WIDTH = 60;

const STORAGE_PREFIX = 'ds-data-table';

export type ColumnWidths = Record<string, number>;

const storageKeyFor = (key: string) => `${STORAGE_PREFIX}:${key}:column-widths`;

/**
 * localStorage is unavailable in some privacy modes and throws rather than
 * returning null, so every access is guarded — a table must never fail to render
 * because a width could not be remembered.
 */
const readStored = (key?: string): ColumnWidths => {
  if (!key || typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage.getItem(storageKeyFor(key));
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    // Drop anything that isn't a usable number — stored data may be stale or
    // hand-edited, and a NaN width would collapse the column.
    return Object.entries(parsed as Record<string, unknown>).reduce<ColumnWidths>((acc, [columnKey, value]) => {
      if (typeof value === 'number' && Number.isFinite(value) && value >= MIN_COLUMN_WIDTH) {
        acc[columnKey] = value;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const writeStored = (key: string, widths: ColumnWidths) => {
  try {
    window.localStorage.setItem(storageKeyFor(key), JSON.stringify(widths));
  } catch {
    // Persisting is best-effort; resizing still works for this session.
  }
};

interface UseColumnWidthsParams {
  storageKey?: string;
  onColumnResize?: (columnKey: string, width: number) => void;
  /** Whether a resize affordance is rendered at all, so the hint below is relevant. */
  resizeEnabled?: boolean;
  /** Table name used to identify which table the hint refers to. */
  debugLabel?: string;
}

/** Warn once per table, so a re-rendering table cannot spam the console. */
const warnedKeys = new Set<string>();

/** Test-only: lets a spec assert the warning without depending on test order. */
export const resetStorageKeyWarnings = () => warnedKeys.clear();

/**
 * Persisting widths needs a name to save under, and the component cannot derive a
 * safe one: mount order is unstable, column keys collide between tables, and
 * several pages in this app render two or three tables at once. A guessed key
 * would silently leak one table's widths into another, so the caller must name it
 * — this nudge makes forgetting visible instead of quietly losing the preference.
 */
const warnMissingStorageKey = (ariaLabel?: string) => {
  if (process.env.NODE_ENV === 'production') return;

  const identity = ariaLabel ?? 'unnamed';
  if (warnedKeys.has(identity)) return;
  warnedKeys.add(identity);

  // eslint-disable-next-line no-console
  console.warn(
    `[DataTable] Columns are resizable but no \`columnWidthStorageKey\` was set (table: "${identity}"), ` +
      'so the widths a user drags will be lost on reload. Pass a key unique to this table, ' +
      'e.g. columnWidthStorageKey="settings.users". Pass `resizableColumns={false}` if resizing isn\'t wanted.'
  );
};

export const useColumnWidths = ({ storageKey, onColumnResize, resizeEnabled, debugLabel }: UseColumnWidthsParams) => {
  // Read once on mount rather than in an effect, so the first paint already uses
  // the remembered widths and columns don't visibly jump.
  const [widths, setWidths] = useState<ColumnWidths>(() => readStored(storageKey));
  const [resizingKey, setResizingKey] = useState<string | null>(null);

  /** Live `<col>` nodes, written to directly while a drag is in flight. */
  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  /** The `<table>`, whose min-width has to track the widths during a drag. */
  const tableRef = useRef<HTMLTableElement | null>(null);

  /**
   * Widths are keyed per table, so switching which table this is means the
   * remembered widths belong to something else. Without this, a table that stays
   * mounted while its key changes would keep the previous table's widths in state
   * and then write them out under the new key, corrupting it.
   */
  const previousStorageKey = useRef(storageKey);
  useEffect(() => {
    if (previousStorageKey.current === storageKey) return;

    previousStorageKey.current = storageKey;
    setWidths(readStored(storageKey));
  }, [storageKey]);

  // Warn from an effect rather than during render: a render-phase guard gets
  // consumed by StrictMode's discarded first pass, so the warning never fires.
  useEffect(() => {
    if (resizeEnabled && !storageKey) {
      warnMissingStorageKey(debugLabel);
    }
  }, [debugLabel, resizeEnabled, storageKey]);

  /**
   * Snapshot taken when a drag starts, so a preview can be expressed as a delta
   * from where the drag began rather than from whatever the DOM currently says.
   */
  const dragBaseline = useRef<{ startWidth: number; tableMinWidth: number } | null>(null);

  /** Registers the `<col>` a column's live width is written to during a drag. */
  const registerCol = useCallback((columnKey: string, node: HTMLTableColElement | null) => {
    colRefs.current[columnKey] = node;
  }, []);

  /** Registers the `<table>`, so its min-width can be previewed during a drag. */
  const registerTable = useCallback((node: HTMLTableElement | null) => {
    tableRef.current = node;
  }, []);

  const beginResize = useCallback((columnKey: string, startWidth: number) => {
    const tableMinWidth = tableRef.current ? parseFloat(tableRef.current.style.minWidth) || 0 : 0;

    dragBaseline.current = { startWidth, tableMinWidth };
    setResizingKey(columnKey);
  }, []);

  const endResize = useCallback(() => {
    dragBaseline.current = null;
    setResizingKey(null);
  }, []);

  /**
   * Live update while dragging. Pointermove fires far faster than React can
   * usefully re-render, so the width is written straight to the `<col>` element
   * and only committed to state on release. Going through state here would
   * re-render every row on every pointer event and stall the drag.
   */
  const previewWidth = useCallback((columnKey: string, width: number) => {
    const next = Math.max(MIN_COLUMN_WIDTH, Math.round(width));
    const col = colRefs.current[columnKey];

    if (col) {
      col.style.width = `${next}px`;
    }

    /*
     * The table's min-width is what makes it scroll horizontally rather than
     * squeeze columns, and it comes from React state — which is deliberately not
     * updated mid-drag. Left alone it would stay at its pre-drag value for the
     * whole gesture, so widening a column past the container would shrink its
     * neighbours and then snap on release. Previewing it here keeps the drag
     * honest; state catches up on commit.
     */
    const table = tableRef.current;
    const baseline = dragBaseline.current;

    if (table && baseline?.tableMinWidth) {
      table.style.minWidth = `${baseline.tableMinWidth + (next - baseline.startWidth)}px`;
    }
  }, []);

  /** Called once the drag ends: persist and notify. */
  const commitWidth = useCallback(
    (columnKey: string, width: number) => {
      const next = Math.max(MIN_COLUMN_WIDTH, Math.round(width));

      setWidths((current) => {
        const updated = { ...current, [columnKey]: next };
        if (storageKey) writeStored(storageKey, updated);
        return updated;
      });

      onColumnResize?.(columnKey, next);
    },
    [onColumnResize, storageKey]
  );

  /** Clears every override, returning columns to their declared widths. */
  const resetWidths = useCallback(() => {
    // Inline widths written during past drags survive a state reset, since React
    // only rewrites the ones it set. Clear them so the declared widths apply.
    Object.values(colRefs.current).forEach((col) => {
      if (col) col.style.removeProperty('width');
    });

    setWidths({});
    if (storageKey) writeStored(storageKey, {});
  }, [storageKey]);

  return useMemo(
    () => ({
      widths,
      previewWidth,
      commitWidth,
      resetWidths,
      registerCol,
      registerTable,
      beginResize,
      endResize,
      resizingKey,
    }),
    [beginResize, commitWidth, endResize, previewWidth, registerCol, registerTable, resetWidths, resizingKey, widths]
  );
};
